
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { PromoBanner } from '@/models';
import { ui } from '@/constants';
import { OptionalColorStyle } from '@/models/util';
import { HomePageLinkInteractions } from '@/services/analytics/platforms/adobe-analytics/constants';
import { getAemPromoInteraction } from '@/services/analytics/platforms/adobe-analytics/helpers';
import { PreHideStyleID } from '@/services/adobe-target/adobe-target.service';

@Component({
  name: 'CmgHeroPromoBanner',
})
export default class CmgHeroPromoBanner extends Vue {
  @Prop({ type: Object })
  promoBanner?: PromoBanner;

  @Prop({type: String})
  cmgPromoImageId?: string;

  @Prop({type: String})
  cmgPromoVideoId?: string;

  @Prop({ type: String })
  sectionSuffix?: string;

  get dataSectionName(): string {
    const suffix = this.sectionSuffix ? `-${this.sectionSuffix}` : '';
    return `hero-promo-banner${suffix}`;
  }

  get showPromoBanner(): boolean {
    return this.promoBanner ? this.promoBanner.showPromoBanner : false;
  }
  
  get centerAlignedTextOnly(): boolean {
    return this.promoBanner ? this.promoBanner.centerAlignedTextOnly : false;
  }

  get isContentRightAligned(): boolean {
    return this.promoBanner ? this.promoBanner.isContentRightAligned : false;
  }

  get isDesktop(): boolean {
    return this.$appStore.state.ui.clientWidth > ui.breakpoints.tabletMax;
  }

  get backgroundImageUrl(): string | undefined {
    return this.promoBanner && this.promoBanner.primary.backgroundImageUrl;
  }

  get backgroundVideoUrl(): string | undefined {
    return this.promoBanner && this.promoBanner.primary.backgroundVideoUrl;
  }

  get shouldLoopVideo(): boolean | undefined {
    return this.promoBanner ? this.promoBanner.primary.shouldLoopVideo : false;
  }

  get customerInfoHydrated(): boolean {
    return this.$services.customer.isLoggedIn() != undefined;
  }

  get preTitle(): string | undefined {
    return this.promoBanner && this.promoBanner.primary.preTitle;
  }

  get preTitleColor(): OptionalColorStyle {
    return this.promoBanner && this.promoBanner.primary.preTitleColor ? { color: this.promoBanner.primary.preTitleColor } : {};
  }

  get title(): string | undefined {
    return this.promoBanner && this.promoBanner.primary.title;
  }

  get titleColor(): OptionalColorStyle {
    return this.promoBanner && this.promoBanner.primary.titleColor ? { color: this.promoBanner.primary.titleColor } : {};
  }

  get subtitle(): string | undefined {
    return this.promoBanner && this.promoBanner.primary.subtitle;
  }

  get subtitleColor(): OptionalColorStyle {
    return this.promoBanner && this.promoBanner.primary.subtitleColor ? { color: this.promoBanner.primary.subtitleColor } : {};
  }

  get legal(): string | undefined {
    return this.promoBanner && this.promoBanner.primary.legalText;
  }

  get legalColor(): OptionalColorStyle {
    return this.promoBanner && this.promoBanner.primary.legalTextColor ? { color: this.promoBanner.primary.legalTextColor } : {};
  }

  get image(): string | undefined {
    // imageUrlDesktop is used for both desktop and mobile in the single promo banner - I'm sorry
    return this.promoBanner && this.promoBanner.primary.imageUrlDesktop;
  }

  get navigationLink(): string | undefined {
    return this.promoBanner && this.promoBanner.primary.buttonLink;
  }

  get buttonText(): string | undefined {
    return this.promoBanner && this.promoBanner.primary.buttonText;
  }

  get buttonColor(): object {
    return this.promoBanner && this.promoBanner.primary.buttonColor ? { border: 0, backgroundColor: this.promoBanner.primary.buttonColor } : {};
  }

  get showButton(): boolean {
    return !!(this.buttonText && this.navigationLink);
  }

  get secondaryNavigationLink(): string | undefined {
    return this.promoBanner && this.promoBanner.secondary && this.promoBanner.secondary.buttonLink;
  }

  get secondaryButtonText(): string | undefined {
    return this.promoBanner && this.promoBanner.secondary && this.promoBanner.secondary.buttonText;
  }

  get secondaryButtonColor(): object {
    return this.promoBanner && this.promoBanner.secondary && this.promoBanner.secondary.buttonColor
      ? { border: 0, backgroundColor: this.promoBanner.secondary.buttonColor }
      : {};
  }

  get showSecondaryButton(): boolean {
    return !!(this.secondaryButtonText && this.secondaryNavigationLink);
  }

  get shouldPlaceMobileTitleBelowImage(): boolean {
    return this.promoBanner ? this.promoBanner.shouldPlaceMobileTitleBelowImage : true;
  }

  mounted() {
    this.$services.adobeTarget.prehideContent(PreHideStyleID.heroPromoBanner);
  }
  
  navigateTo(section: string): void {
    this.$services.analytics.addLinkClickedEventToStore(getAemPromoInteraction(HomePageLinkInteractions.HeroBannerOrderNow, this.promoBanner), section);
    // Join Now button click back focus should go to greetings
    this.$services.ui.setLastSelectedElement('customer-greeting');
    location.href = section || '';
  }
}
